import React from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { IAddProductInput, ICategory, IProduct } from "muminsmart-utils";
import { ChevronDown } from "lucide-react";
import {
  createRandomSku,
  formatVariantAttributeSummary,
  useCategoryVariantAttributes,
} from "@/app/components/product/utils";
import VariantMatrixGenerator from "./VariantMatrixGenerator";
import VirtualizedVariantList from "./VirtualizedVariantList";
import VariantValidationSummary from "./VariantValidationSummary";
import CustomImage from "../../common/Image";
import { productVariantBg } from "@/assets";
import ConfirmDialog from "../../common/ConfirmAlert";
import { useConfirm } from "@/hooks/use-confirm";

interface ProductVariantsProps {
  categories: ICategory[];
  productAlwaysAvailable: boolean;
  selectedProduct: IProduct | null;
}

const ProductVariants: React.FC<ProductVariantsProps> = ({
  categories,
  productAlwaysAvailable,
  selectedProduct,
}) => {
  const form = useFormContext<IAddProductInput>();
  const {
    isOpen: isConfirmOpen,
    title: confirmTitle,
    description: confirmDescription,
    confirm,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  const bottomRef = React.useRef<HTMLDivElement>(null);

  const productVariants = useFieldArray({
    control: form.control,
    name: "variants",
  });

  const selectedSubSubCategoryIds = useWatch({
    control: form.control,
    name: "subSubCategoryIds",
  });

  const watchedProductVariants = useWatch({
    control: form.control,
    name: "variants",
  });

  const { variantAttributes, attributeNameById } = useCategoryVariantAttributes(
    categories,
    selectedSubSubCategoryIds,
    selectedProduct?.variants,
  );

  React.useEffect(() => {
    if (!variantAttributes.length) {
      return;
    }

    const currentVariants = form.getValues("variants") ?? [];

    currentVariants.forEach((variant, variantIndex) => {
      const currentVariantAttributes = variant?.variantAttributes ?? [];

      const realignedAttributes = variantAttributes.map(({ attribute }) => {
        const existingAttribute = currentVariantAttributes.find(
          (item) => item.attributeId === attribute.id,
        );

        return {
          attributeId: attribute.id,
          value: existingAttribute?.value ?? "",
        };
      });

      const isSameOrderAndLength =
        currentVariantAttributes.length === realignedAttributes.length &&
        currentVariantAttributes.every(
          (item, idx) =>
            item.attributeId === realignedAttributes[idx].attributeId,
        );

      if (!isSameOrderAndLength) {
        form.setValue(
          `variants.${variantIndex}.variantAttributes`,
          realignedAttributes,
          { shouldDirty: true, shouldTouch: true },
        );
      }
    });
  }, [form, variantAttributes]);

  const createEmptyVariant = React.useCallback(
    () => ({
      variantAttributes: variantAttributes.map((variantAttribute) => ({
        attributeId: variantAttribute.attribute.id,
        value: "",
      })),
      weight: 0,
      price: 0,
      discountedPrice: 0,
      bulkPrices: [],
      sku: "",
      stock: 0,
      featuredImage: undefined as unknown as File,
      additionalImages: [],
      isActive: true,
      alwaysAvailable: productAlwaysAvailable,
    }),
    [productAlwaysAvailable, variantAttributes],
  );

  const cloneVariant = React.useCallback(
    (sourceVariant: NonNullable<
      (typeof watchedProductVariants)[number]
    >) => ({
      variantAttributes: (sourceVariant.variantAttributes ?? []).map(
        (attribute) => ({
          attributeId: attribute.attributeId,
          value: attribute.value,
        }),
      ),
      weight: Number(sourceVariant.weight ?? 0),
      price: Number(sourceVariant.price ?? 0),
      discountedPrice: Number(sourceVariant.discountedPrice ?? 0),
      bulkPrices: (sourceVariant.bulkPrices ?? []).map((bulkPrice) => ({
        quantity: Number((bulkPrice as { quantity?: number }).quantity ?? 1),
        price: Number(bulkPrice.price ?? 0),
      })),
      sku: "",
      stock: Number(sourceVariant.stock ?? 0),
      featuredImage: sourceVariant.featuredImage,
      additionalImages: [...(sourceVariant.additionalImages ?? [])],
      isActive: true,
      alwaysAvailable:
        sourceVariant.alwaysAvailable ?? productAlwaysAvailable,
    }),
    [productAlwaysAvailable],
  );

  const handleAddVariant = React.useCallback(() => {
    if (productVariants.fields.length === 0) {
      productVariants.append(createEmptyVariant());
      return;
    }

    const lastVariant =
      watchedProductVariants[watchedProductVariants.length - 1];

    if (!lastVariant) {
      return;
    }

    productVariants.append(cloneVariant(lastVariant));
  }, [
    cloneVariant,
    createEmptyVariant,
    productVariants,
    watchedProductVariants,
  ]);

  const handleCopyVariant = React.useCallback(
    (index: number) => {
      const sourceVariant = watchedProductVariants[index];

      if (!sourceVariant) {
        return;
      }

      productVariants.insert(index + 1, cloneVariant(sourceVariant));
    },
    [cloneVariant, productVariants, watchedProductVariants],
  );

  const requestDeleteVariant = React.useCallback(
    async (index: number) => {
      const variant = watchedProductVariants[index];
      const variantSummary = formatVariantAttributeSummary(
        variant,
        attributeNameById,
      );

      const confirmed = await confirm({
        title: "Delete variant?",
        description: variantSummary
          ? `This will permanently remove "${variantSummary}".`
          : `This will permanently remove Variant ${index + 1}.`,
      });

      if (!confirmed) {
        return;
      }

      productVariants.remove(index);
    },
    [
      attributeNameById,
      confirm,
      productVariants,
      watchedProductVariants,
    ],
  );

  const handleAddBulkPrice = React.useCallback(
    (variantIndex: number) => {
      const currentBulkPrices =
        form.getValues(`variants.${variantIndex}.bulkPrices`) ?? [];

      form.setValue(
        `variants.${variantIndex}.bulkPrices`,
        [...currentBulkPrices, { quantity: 1, price: 0 }],
        { shouldDirty: true, shouldTouch: true },
      );
    },
    [form],
  );

  const handleDeleteBulkPrice = React.useCallback(
    (variantIndex: number, bulkPriceIndex: number) => {
      const currentBulkPrices =
        form.getValues(`variants.${variantIndex}.bulkPrices`) ?? [];

      form.setValue(
        `variants.${variantIndex}.bulkPrices`,
        currentBulkPrices.filter((_, index) => index !== bulkPriceIndex),
        { shouldDirty: true, shouldTouch: true },
      );
    },
    [form],
  );

  const handleGenerateSku = React.useCallback(
    (variantIndex: number) => {
      const existingSkus = (form.getValues("variants") ?? [])
        .filter((_, index) => index !== variantIndex)
        .map((variant) => variant?.sku ?? "");

      form.setValue(
        `variants.${variantIndex}.sku`,
        createRandomSku(existingSkus),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    },
    [form],
  );

  const scrollToBottom = React.useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  return (
    <div className="flex w-full min-w-0 max-w-full flex-1 flex-col items-center justify-center overflow-x-hidden">
      <VariantMatrixGenerator
        categories={categories}
        selectedSubSubCategoryIds={selectedSubSubCategoryIds ?? []}
        productAlwaysAvailable={productAlwaysAvailable}
      />

      {productVariants.fields.length === 0 ? (
        <CustomImage
          src={productVariantBg}
          alt="variant"
          className="w-[80%] max-w-full object-contain opacity-80"
        />
      ) : (
        <div className="flex w-full flex-col gap-4">
          <VariantValidationSummary variants={watchedProductVariants} />

          <VirtualizedVariantList
            form={form}
            fields={productVariants.fields}
            variantAttributes={variantAttributes}
            onCopyVariant={handleCopyVariant}
            onRequestDeleteVariant={(index) => void requestDeleteVariant(index)}
            onGenerateSku={handleGenerateSku}
            onAddBulkPrice={handleAddBulkPrice}
            onDeleteBulkPrice={handleDeleteBulkPrice}
          />
        </div>
      )}

      <button
        type="button"
        onClick={handleAddVariant}
        className="mt-8 w-full rounded-full border-2 border-dashed border-primary py-3 font-medium text-primary transition hover:bg-amber-50"
      >
        + Add Variant
      </button>

      <div ref={bottomRef} className="h-0 w-full" aria-hidden="true" />

      <button
        type="button"
        onClick={scrollToBottom}
        aria-label="Scroll to bottom"
        className="fixed bottom-8 right-8 z-20 flex p-2 items-center justify-center rounded-full bg-primary text-white shadow-[0_10px_30px_rgba(15,69,80,0.35)]"
      >
        <ChevronDown size={20} />
      </button>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={confirmTitle}
        description={confirmDescription}
        confirmButtonLabel="Delete"
        cancelButtonLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default ProductVariants;
